import { Fragment, ReactElement, useContext, useEffect, useState } from 'react';
import Buttons from '@components/Buttons';
import Button from '@components/DesignLibrary/Button';
import Spacer from '@components/Spacer';
import PlusCircle from '@components/Icons/PlusCircle';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@typings/api-models';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ListCell from '@components/DesignLibrary/ListCell';
import CheckBox from '@components/DesignLibrary/Atoms/CheckBox';
import { WorkflowOwner } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import TeamsContext from '@/providers/Teams/TeamsContext';
import s from './AssignCollaboratorsToAttachmentForm.module.scss';
import SearchInput from '../FormFields/SearchInput';

export type Collaborator = {
    id: string;
    logo?: string | null;
    color?: string | null;
    name?: string | null
    email?: string | null;
    defaultWorkflowRole?: string | null;
  };

type Props = {
  attachmentType?: MESSAGE_ATTACHMENT_REFERENCE_TYPES;
  attachmentReferenceId?: string;
  collaborators: Collaborator[];
  isSubmitting: boolean;
  onSubmit: (
    owners: WorkflowOwner[],
  ) => void;
  onInviteClick?: () => void;
  initialCollaborators?: WorkflowOwner[];
  hasPadding?: boolean;
  hideRoles?: boolean;
};

const AssignCollaboratorsToAttachmentForm = ({
  onSubmit,
  onInviteClick,
  isSubmitting,
  collaborators,
  attachmentReferenceId,
  attachmentType,
  initialCollaborators,
  hasPadding = true,
  hideRoles = false,
}: Props): ReactElement => {
  const [assignedCollaboratorIds, setAssignedCollaboratorIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [workflowRoleMapping, setWorkflowRoleMapping] = useState<Record<string, string[]>>({});

  const { workflowRoles, fetchWorkflowRoles } = useContext(TeamsContext);

  const { getData: getDocumentRequestUserPermissionsFn } = useNoodleApi(tsClient.documentRequests.getDocumentRequestUserPermissions);
  const { getData: getFormRequestPermissionsFn } = useNoodleApi(tsClient.formRequests.getFormRequestPermissions);
  const { getData: getInvoicePermissions } = useNoodleApi(tsClient.invoices.getInvoicePermissions);
  const { getData: getCustomTermsPermissions } = useNoodleApi(tsClient.customTerms.getCustomTermsPermissions);
  const { getData: getBookingPermissions } = useNoodleApi(tsClient.scheduling.getBookingPermissions);
  const { getData: getCreditReportPermissionsFn } = useNoodleApi(tsClient.creditReportsB2C.getCreditReportPermissions);
  const { getData: getBankAccountIntegrationPermissions } = useNoodleApi(tsClient.bankAccountIntegrations.getBankAccountIntegrationPermissions);

  const flipCollaboratorAssignment = (collaborator: Collaborator): void => {
    if (assignedCollaboratorIds.includes(collaborator.id)) {
      // remove collaborator from mapping
      setWorkflowRoleMapping(prev => {
        const updatedMapping = { ...prev };
        delete updatedMapping[collaborator.id];
        return updatedMapping;
      });
      setAssignedCollaboratorIds(prev => prev.filter(id => id !== collaborator.id));
    } else {
      setAssignedCollaboratorIds(prev => [...prev, collaborator.id]);
      setWorkflowRoleMapping(prev => ({
        ...prev,
        [collaborator.id]: collaborator.defaultWorkflowRole ? [collaborator.defaultWorkflowRole] : [],
      }));
    }
  };

  const assignRolesToCollaborators = ({
    collaboratorId,
    role,
    isChecked,
  }: {
    collaboratorId: string;
    role: string;
    isChecked: boolean;
  }): void => {
    setWorkflowRoleMapping(prev => {
      const updatedMapping = { ...prev };
      if (!updatedMapping[collaboratorId]) {
        updatedMapping[collaboratorId] = [];
      }
      if (isChecked) {
        if (!updatedMapping[collaboratorId].includes(role)) {
          updatedMapping[collaboratorId].push(role);
        }
      } else {
        updatedMapping[collaboratorId] = updatedMapping[collaboratorId].filter(r => r !== role);
      }
      return updatedMapping;
    });
  };

  useEffect(() => {
    const workflowRoleMappingLocal: Record<string, string[]> = { ...workflowRoleMapping };
    (assignedCollaboratorIds || []).forEach((collaboratorId) => {
      if (!workflowRoleMapping[collaboratorId]) {
        workflowRoleMappingLocal[collaboratorId] = [];
      }
    });
    setWorkflowRoleMapping(workflowRoleMappingLocal);
  }, [assignedCollaboratorIds]);

  useEffect(() => {
    const getAssignedCollaborators = async (): Promise<void> => {
      if (!attachmentType || !attachmentReferenceId) {
        return;
      }
      if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.DOCUMENT_REQUEST_USER) {
        const { data: documentRequestPermissions } = await getDocumentRequestUserPermissionsFn({ id: attachmentReferenceId });
        if (documentRequestPermissions) {
          const assignedUserIds = documentRequestPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CREDIT_REPORT) {
        const { data: creditReportPermissions } = await getCreditReportPermissionsFn({ creditReportId: attachmentReferenceId });
        if (creditReportPermissions) {
          const assignedUserIds = creditReportPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.FORM_REQUEST) {
        const { data: formRequestPermissions } = await getFormRequestPermissionsFn({ formRequestId: attachmentReferenceId });
        if (formRequestPermissions) {
          const assignedUserIds = formRequestPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.INVOICE) {
        const { data: invoicePermissions } = await getInvoicePermissions({ invoiceId: attachmentReferenceId });
        if (invoicePermissions) {
          const assignedUserIds = invoicePermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.CUSTOM_TERMS) {
        const { data: termsPermissions } = await getCustomTermsPermissions({ id: attachmentReferenceId });
        if (termsPermissions) {
          const assignedUserIds = termsPermissions.map((p) => p.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BOOKING) {
        const { data: bookingPermissions } = await getBookingPermissions({ id: attachmentReferenceId });
        if (bookingPermissions) {
          const assignedUserIds = bookingPermissions.map((p) => p.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      } else if (attachmentType === MESSAGE_ATTACHMENT_REFERENCE_TYPES.BANK_ACCOUNT_INTEGRATION) {
        const { data: bankAccountIntegrationPermissions } = await getBankAccountIntegrationPermissions(
          { bankAccountIntegrationId: attachmentReferenceId });
        if (bankAccountIntegrationPermissions) {
          const assignedUserIds = bankAccountIntegrationPermissions.map((permission) => permission.personId);
          setAssignedCollaboratorIds(assignedUserIds);
        }
      }
    };
    getAssignedCollaborators();
  }, [
    getInvoicePermissions,
    getDocumentRequestUserPermissionsFn,
    getFormRequestPermissionsFn,
    getCreditReportPermissionsFn,
    attachmentType,
    attachmentReferenceId,
    getCustomTermsPermissions,
    getBookingPermissions,
    getBankAccountIntegrationPermissions,
  ]);

  useEffect(() => {
    // Initialize workflowRoleMapping with initialCollaborators
    const initialMapping: Record<string, string[]> = {};
    const initialCollaboratorsIdsLocal: string[] = [];
    (initialCollaborators || []).forEach((collaborator) => {
      initialMapping[collaborator.personId] = collaborator.workflowRoles || [];
      initialCollaboratorsIdsLocal.push(collaborator.personId);
    });
    setWorkflowRoleMapping(initialMapping);
    setAssignedCollaboratorIds(initialCollaboratorsIdsLocal);
  }, [initialCollaborators]);

  useEffect(() => {
    fetchWorkflowRoles();
  }, []);

  const transformBody = (workflowRoleMappingLocal: Record<string, string[]>): WorkflowOwner[] => {
    const roleMapping: WorkflowOwner[] = [];
    Object.entries(workflowRoleMappingLocal || []).forEach(([collaboratorId, roles]) => {
      roleMapping.push({
        personId: collaboratorId,
        workflowRoles: roles as Array<'Intake Lead' | 'Attorney' | 'Paralegal' | 'Documents Team'> || [],
      });
    });
    return roleMapping;
  };

  const WorkflowRolesComponent = ({ collaborator }: { collaborator: Collaborator }): ReactElement => (
    <div className={s.container}>
      {assignedCollaboratorIds.includes(collaborator.id) && (
        <fieldset className={s.workflowRoles}>
          <legend>Workflow roles:</legend>
          {workflowRoles.map((role, index) => (
            <Fragment key={`role-${index}`}>
              <CheckBox
                label={role.name}
                className={s.workflowRoleCheckbox}
                size="sm"
                onChange={(value) => {
                  assignRolesToCollaborators({
                    collaboratorId: collaborator.id,
                    isChecked: value,
                    role: role.id,
                  });
                }}
                isChecked={
                  workflowRoleMapping[collaborator.id]?.includes(role.id) || false
                }
              />
              <Spacer size={4} />
            </Fragment>
          ))}
        </fieldset>
      )}
    </div>
  );

  /**
   * Sorts collaborators array by prioritizing initial collaborators first.
   * @param cc - Array of collaborators to sort
   * @param icc - Optional array of initial collaborators to prioritize
   * @returns Sorted array with initial collaborators first, maintaining original order otherwise
   */
  const sortCollaborators = (cc: Collaborator[], icc?: WorkflowOwner[]): Collaborator[] => {
    if (!icc) return cc;

    const initialCollaboratorIds = icc.map(c => c.personId);
    return [...collaborators].sort((a, b) => {
      const aIsInitial = initialCollaboratorIds.includes(a.id);
      const bIsInitial = initialCollaboratorIds.includes(b.id);

      if (aIsInitial && !bIsInitial) return -1;
      if (!aIsInitial && bIsInitial) return 1;
      return 0;
    });
  };

  const sortedCollaborators = sortCollaborators(collaborators, initialCollaborators);

  return (
    <>
      <div className={s.assignCollaborator} style={{ padding: hasPadding ? 16 : 0 }}>
        {collaborators.length > 0 && (
          <div className={s['collaborators-container']}>
            <SearchInput label="" placeholder="Filter by name or email" setSearch={setSearch} className={s.search} />
            {sortedCollaborators
              .filter(collaborator => (collaborator.email?.includes(search) || collaborator.name?.includes(search)))
              .map(collaborator => (
                <ListCell
                  key={collaborator.id}
                  avatar={{
                    color: collaborator.color || '',
                    name: collaborator.name || '',
                    url: collaborator.logo || '',
                  }}
                  label={collaborator.name || 'No name'}
                  bottomLine={collaborator.email || ''}
                  showCheckbox
                  isSelected={assignedCollaboratorIds.includes(collaborator.id)}
                  onClick={() => flipCollaboratorAssignment(collaborator)}
                  additionalComponents={!hideRoles
                    ? [
                      {
                        component: <WorkflowRolesComponent collaborator={collaborator} />,
                        key: `workflow-role${collaborator.id}`,
                      },
                    ]
                    : []}
                />
              ))}
          </div>
        )}
        {onInviteClick && (
          <>
            <Spacer size={16}/>
            <Buttons isExtraSmall isWrapper className={s.invite} onClick={onInviteClick}>
              <>
                <PlusCircle size={8} weight="fill" />
                Invite Collaborator and Assign
              </>
            </Buttons>
          </>
        )}
      </div>
      <div className={s.buttonContainer}>
        <Button
          variant='primary'
          size='md'
          onClick={() => onSubmit(transformBody(workflowRoleMapping))}
          loading={isSubmitting}
          fullWidth
        >
          Save
        </Button>
      </div>
    </>
  );
};

export default AssignCollaboratorsToAttachmentForm;
